import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  getWallet,
  createRechargeCheckout,
  confirmRecharge,
  getTransactions,
  redeemFreeMinutes,
  getRechargePackages,
  getWalletBonuses,
  type WalletTransactionQueryParams,
} from '../services/wallet.service';
import { isFirstRechargeOfferEligible } from '../lib/firstRechargePromo';

export function useWallet() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['wallet'],
    queryFn: getWallet,
    enabled: isAuthenticated,
  });
}

/**
 * Create a Stripe Checkout session for a top-up.
 * The component is responsible for redirecting to `data.url`
 * (e.g. `window.location.assign(url)`).
 */
export function useCreateRechargeCheckout() {
  return useMutation({
    mutationFn: (amount: number) => createRechargeCheckout(amount),
  });
}

/**
 * Confirm a recharge on return from Stripe Checkout.
 * On success, invalidates the wallet + transactions queries.
 */
export function useConfirmRecharge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => confirmRecharge(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
    },
  });
}

export function useWalletTransactions(params?: WalletTransactionQueryParams) {
  return useQuery({
    queryKey: ['wallet-transactions', params],
    queryFn: () => getTransactions(params),
    placeholderData: keepPreviousData,
  });
}

export function useRedeemFreeMinutes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => redeemFreeMinutes(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
    },
  });
}

/**
 * Active bonus credits grouped by source (welcome / cashback / referral / …),
 * each with its own remaining amount and expiry. Powers "Le mie Promozioni".
 */
export function useWalletBonuses() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['wallet-bonuses'],
    queryFn: getWalletBonuses,
    enabled: isAuthenticated,
    staleTime: 1000 * 60,
  });
}

export function useRechargePackages() {
  return useQuery({
    queryKey: ['recharge-packages'],
    queryFn: getRechargePackages,
    staleTime: 1000 * 60 * 60, // 1 hour - rarely changes
  });
}

/**
 * "Bonus Prima Ricarica" (Art. 6.8) eligibility.
 *
 * Drives the struck-through 0,99 €/min price on consultant cards/profiles.
 * Anonymous (not-logged-in) visitors still see the promo price as a teaser.
 * Logged-in users see it only while their first-recharge promo is active.
 */
export function usePromoEligibility() {
  const { isAuthenticated } = useAuth();
  const { data: wallet, isLoading } = useWallet();

  if (!isAuthenticated) {
    return { isPromoEligible: true, isLoading: false };
  }

  if (isLoading || !wallet) {
    return { isPromoEligible: true, isLoading: true };
  }

  return {
    isPromoEligible: isFirstRechargeOfferEligible(isAuthenticated, wallet),
    isLoading: false,
  };
}

/**
 * State for the "Tariffa promozionale attiva" badge: whether the promo is on
 * and how much promo credit remains. Returns inactive while loading/anonymous.
 */
export function useFirstChargePromo() {
  const { isAuthenticated } = useAuth();
  const { data: wallet, isLoading } = useWallet();

  if (!isAuthenticated || isLoading || !wallet || !wallet.firstChargePromoActive) {
    return { isActive: false, promoCreditsRemaining: 0 };
  }

  return {
    isActive: true,
    promoCreditsRemaining: wallet.promoCreditsRemaining ?? 0,
  };
}
